import type { useSnackbar } from 'notistack'
import { PAYMENT_PENDING_MESSAGE } from '@paintscout/util/builder'

/**
 * The pre-submit fresh-state check shared by CreditCardForm/ACHForm — the last sample before
 * PayFields.submit() moves money. Returns true when the payment is blocked, after resetting the
 * form's submitting state, showing the pending snackbar, and closing the dialog; the caller must
 * abort the submit. `checkPaymentBlocked` itself fails open (resolves false on fetch errors), so a
 * blip never blocks a legitimate payment.
 */
export async function abortIfPaymentBlocked(args: {
  checkPaymentBlocked?: () => Promise<boolean>
  submittingRef: { current: boolean }
  setSubmitting: (value: boolean) => void
  setPayFieldsLoading: (value: boolean) => void
  enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar']
  /** The form's `__` translation function. */
  translate: (message: string) => string
  /** Close the whole dialog stack (dismissAllDialogs) — the payment dialog may sit on top of the
   * processing-payment warning dialog, and both must go. */
  closeDialogs?: () => void
}): Promise<boolean> {
  const {
    checkPaymentBlocked,
    submittingRef,
    setSubmitting,
    setPayFieldsLoading,
    enqueueSnackbar,
    translate,
    closeDialogs
  } = args

  if (!checkPaymentBlocked || !(await checkPaymentBlocked())) {
    return false
  }

  submittingRef.current = false
  setSubmitting(false)
  setPayFieldsLoading(false)
  enqueueSnackbar(translate(PAYMENT_PENDING_MESSAGE), { variant: 'error' })
  closeDialogs?.()
  return true
}
